<script setup lang="ts">
/**
 * SettingsView — /settings/:section?
 *
 * Two-column layout: 220px left-nav + minmax(0, 1fr) content.
 * Below 900px: single column with mobile chip-strip nav.
 *
 * D-S1: No brand-color section.
 * D-S6: No API tokens section.
 * D-S9: Shows deletion banner when HOA is soft-deleted.
 */
import { onMounted } from 'vue';
import { useRoute } from 'vue-router';
import ProfileSection from '@/components/settings/ProfileSection.vue';
import PortalSection from '@/components/settings/PortalSection.vue';
import NotificationsSection from '@/components/settings/NotificationsSection.vue';
import BillingSection from '@/components/settings/BillingSection.vue';
import SecuritySection from '@/components/settings/SecuritySection.vue';
import AuditLogSection from '@/components/settings/AuditLogSection.vue';
import DangerZoneSection from '@/components/settings/DangerZoneSection.vue';
import { useSettingsStore } from '@/stores/settings';
import { useSettingsScrollSpy } from '@/composables/useSettingsScrollSpy';
import type { SettingsSection } from '@/composables/useSettingsScrollSpy';

const store = useSettingsStore();
const route = useRoute();

const sectionIds: SettingsSection[] = [
  'profile',
  'portal',
  'notifications',
  'billing',
  'security',
  'audit',
  'danger',
];

const { activeSection, scrollTo } = useSettingsScrollSpy(sectionIds);

onMounted(async () => {
  await store.fetch();

  const sectionParam = route.params.section as string | undefined;
  const hashSection = window.location.hash.replace('#', '') as SettingsSection;
  const initial = (sectionParam ?? hashSection ?? 'profile') as SettingsSection;
  if (sectionIds.includes(initial)) {
    setTimeout(() => scrollTo(initial), 100);
  }
});

function onNavigate(id: SettingsSection) {
  scrollTo(id);
}

// Nav item definitions (D-S6: no API tokens; D-S1: no branding nav sub-item)
interface NavItem { id: SettingsSection; label: string; icon: string; danger?: boolean }

const workspaceItems: NavItem[] = [
  {
    id: 'profile',
    label: 'HOA profile',
    icon: '<path d="M3 11.5L12 4l9 7.5"/><path d="M5 10v10h14V10"/>',
  },
  {
    id: 'portal',
    label: 'Resident portal',
    icon: '<circle cx="12" cy="12" r="9"/><path d="M2 12h20"/>',
  },
  {
    id: 'notifications',
    label: 'My notifications',
    icon: '<path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>',
  },
];

const accountItems: NavItem[] = [
  {
    id: 'billing',
    label: 'Billing',
    icon: '<rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18"/>',
  },
  {
    id: 'security',
    label: 'Security',
    icon: '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 1 1 8 0v4"/>',
  },
];

const otherItems: NavItem[] = [
  {
    id: 'audit',
    label: 'Audit log',
    icon: '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  },
  {
    id: 'danger',
    label: 'Danger zone',
    icon: '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><path d="M12 9v4M12 17h.01"/>',
    danger: true,
  },
];

const allNavItems = [...workspaceItems, ...accountItems, ...otherItems];

function navItemClass(item: NavItem): string {
  const isActive = activeSection.value === item.id;
  const base = 'flex items-center gap-2 px-3 py-2 rounded-md text-[13.5px] font-medium transition-colors cursor-pointer w-full text-left';
  if (item.danger) {
    return `${base} ${isActive ? 'bg-red-50 text-red-700 font-semibold' : 'text-red-700 hover:bg-red-50'}`;
  }
  return `${base} ${isActive ? 'bg-navy-50 text-navy-800 font-semibold' : 'text-fg-soft hover:bg-canvas-soft hover:text-fg'}`;
}
</script>

<template>
  <div class="flex flex-col gap-4 pb-12" data-testid="settings-view">
    <!-- Page header -->
    <div class="flex flex-col gap-1">
      <nav class="text-sm text-muted flex items-center gap-1.5" aria-label="breadcrumb">
        <a href="/dashboard" class="hover:text-fg transition-colors">Dashboard</a>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
        <span class="text-fg font-medium">Settings</span>
      </nav>
      <h1 class="text-2xl font-bold text-fg tracking-tight">Settings</h1>
      <p class="text-sm text-muted">
        Workspace, notifications, billing, security, and integrations for
        {{ store.profile?.name ?? 'your HOA' }}.
      </p>
    </div>

    <!-- Mobile chip strip (visible <900px) -->
    <div class="settings-mobile-nav overflow-x-auto pb-1 -mx-4 px-4">
      <div class="flex gap-1.5 w-max">
        <button
          v-for="item in allNavItems"
          :key="item.id"
          :data-testid="`settings-chip-${item.id}`"
          :aria-current="activeSection === item.id ? 'location' : undefined"
          class="shrink-0 px-3 h-8 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors"
          :class="
            activeSection === item.id
              ? item.danger
                ? 'bg-red-50 text-red-700'
                : 'bg-navy-50 text-navy-800'
              : item.danger
                ? 'text-red-700 border border-red-100'
                : 'bg-canvas-soft text-fg-soft border border-border'
          "
          @click="onNavigate(item.id)"
        >
          {{ item.label }}
        </button>
      </div>
    </div>

    <!-- Main grid -->
    <div class="settings-layout">
      <!-- Desktop sidebar (hidden <900px) -->
      <aside class="settings-desktop-nav">
        <nav aria-label="Settings sections" class="flex flex-col gap-0.5 sticky top-[84px]" data-testid="settings-desktop-nav">
          <span class="px-3 pt-3 pb-1.5 text-[11px] font-bold tracking-widest uppercase text-muted">Workspace</span>
          <button
            v-for="item in workspaceItems"
            :key="item.id"
            :class="navItemClass(item)"
            :data-testid="`settings-nav-${item.id}`"
            :aria-current="activeSection === item.id ? 'location' : undefined"
            @click="onNavigate(item.id)"
          >
            <!-- eslint-disable-next-line vue/no-v-html — icon is a static hardcoded SVG path string, not user input -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0" aria-hidden="true" v-html="item.icon" />
            {{ item.label }}
          </button>

          <span class="px-3 pt-3 pb-1.5 text-[11px] font-bold tracking-widest uppercase text-muted">Account</span>
          <button
            v-for="item in accountItems"
            :key="item.id"
            :class="navItemClass(item)"
            :data-testid="`settings-nav-${item.id}`"
            :aria-current="activeSection === item.id ? 'location' : undefined"
            @click="onNavigate(item.id)"
          >
            <!-- eslint-disable-next-line vue/no-v-html — icon is a static hardcoded SVG path string, not user input -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0" aria-hidden="true" v-html="item.icon" />
            {{ item.label }}
          </button>

          <span class="px-3 pt-3 pb-1.5 text-[11px] font-bold tracking-widest uppercase text-muted">Other</span>
          <button
            v-for="item in otherItems"
            :key="item.id"
            :class="navItemClass(item)"
            :data-testid="`settings-nav-${item.id}`"
            :aria-current="activeSection === item.id ? 'location' : undefined"
            @click="onNavigate(item.id)"
          >
            <!-- eslint-disable-next-line vue/no-v-html — icon is a static hardcoded SVG path string, not user input -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0" aria-hidden="true" v-html="item.icon" />
            {{ item.label }}
          </button>
        </nav>
      </aside>

      <!-- Content panels -->
      <div class="flex flex-col gap-5 min-w-0">
        <ProfileSection />
        <PortalSection />
        <NotificationsSection />
        <BillingSection />
        <SecuritySection />
        <AuditLogSection />
        <DangerZoneSection />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Mobile chip strip — only show below 900px */
.settings-mobile-nav { display: block; }

/* Two-column grid — mirrors prototype .settings-shell */
.settings-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.settings-desktop-nav { display: none; }

@media (min-width: 900px) {
  .settings-mobile-nav { display: none; }
  .settings-layout {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    gap: 28px;
    align-items: start;
  }
  .settings-desktop-nav { display: block; }
}
</style>
